import { useQueryClient } from '@tanstack/react-query';
import { DEFAULT_PER_PAGE } from 'mdc-constants';
import { useCallback } from 'react';
import { GetPaginatedFiles, GetPaginatedFilesVariables, UpdateFileMutation, UploadFile } from 'services';

export function useUpdateFilesCache() {
  const queryClient = useQueryClient();

  const updateFilesCacheAfterUpload = useCallback(
    (response: UploadFile, queryVariables: GetPaginatedFilesVariables) => {
      queryClient.cancelQueries({ queryKey: ['getPaginatedFiles', queryVariables] });
      queryClient.setQueryData<GetPaginatedFiles | undefined>(
        ['getPaginatedFiles', queryVariables],
        (oldData: GetPaginatedFiles | undefined) => {
          const newFile = response.upload;

          if (newFile) {
            if (oldData?.files?.data?.length) {
              const newData = [newFile, ...oldData?.files?.data];

              return {
                ...oldData,
                files: {
                  ...oldData?.files,
                  data: newData,
                },
              };
            }

            return {
              ...oldData,
              files: {
                ...oldData?.files,
                data: [newFile],
                paginatorInfo: {
                  count: 1,
                  currentPage: 1,
                  hasMorePages: false,
                  perPage: oldData?.files?.paginatorInfo?.perPage
                    ? oldData?.files?.paginatorInfo?.perPage
                    : DEFAULT_PER_PAGE,
                  total: 1,
                  firstItem: 1,
                  lastItem: 1,
                },
              },
            };
          }
        },
      );
    },
    [queryClient],
  );

  const updateFilesCacheAfterUpdate = useCallback(
    (response: UpdateFileMutation, queryVariables: GetPaginatedFilesVariables) => {
      queryClient.cancelQueries({ queryKey: ['getPaginatedFiles', queryVariables] });
      queryClient.setQueryData<GetPaginatedFiles | undefined>(
        ['getPaginatedFiles', queryVariables],
        (oldData: GetPaginatedFiles | undefined) => {
          const updatedFile = response.updateFile;

          if (updatedFile?.id && oldData?.files?.data?.length) {
            const newData = oldData?.files?.data?.map((file) => (file?.id === updatedFile?.id ? updatedFile : file));

            return {
              ...oldData,
              files: {
                ...oldData?.files,
                data: newData,
              },
            };
          }

          return oldData;
        },
      );
    },
    [queryClient],
  );

  const updateFilesCacheAfterDelete = useCallback(
    (fileIds: string[]) => {
      const queryState = queryClient
        .getQueryCache()
        .findAll({ queryKey: ['getPaginatedFiles'], exact: false, type: 'active' });

      if (queryState.length) {
        queryClient.cancelQueries({ queryKey: ['getPaginatedFiles'], exact: false });
        queryClient.setQueryData<GetPaginatedFiles | undefined>(
          queryState.at(0)?.queryKey as any,
          (oldData: GetPaginatedFiles | undefined) => {
            if (oldData?.files?.data?.length) {
              const newData = oldData?.files?.data?.filter((file) => !fileIds.includes(file?.id));

              return {
                ...oldData,
                files: {
                  ...oldData.files,
                  data: newData,
                  paginatorInfo: {
                    ...oldData?.files?.paginatorInfo,
                    total: oldData?.files?.paginatorInfo?.total - 1,
                    count: oldData?.files?.paginatorInfo?.count - 1,
                    lastItem: oldData?.files?.paginatorInfo?.lastItem
                      ? oldData?.files?.paginatorInfo?.lastItem - 1
                      : undefined,
                  },
                },
              };
            }

            return oldData;
          },
        );
      }
    },
    [queryClient],
  );

  return {
    updateFilesCacheAfterUpload,
    updateFilesCacheAfterDelete,
    updateFilesCacheAfterUpdate,
  };
}
